// NZOS Service Configuration
// This file provides configuration and utilities for NZOS API services

/**
 * Utility function to get the base URL for NZOS services.
 */
export const getNZOSBaseUrl = (): string => {
  // Direct access to webpack-replaced values
  // DefinePlugin will replace these with literal values at build time
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - webpack DefinePlugin will replace this with string literal
  const NZOS_API_URL = process.env.REACT_APP_NZOS_API_URL as string;

  // Try to get production API URL from build-time environment variable
  if (NZOS_API_URL) {
    return NZOS_API_URL;
  }

  // Default fallback - use current origin as base
  return window.location.origin;
};
/**
 * Common headers for NZOS API requests.
 */
export const getNZOSHeaders = (authToken?: string): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  return headers;
};

/**
 * Utility function to build full NZOS API URL.
 */
export const buildNZOSUrl = (endpoint: string, params?: Record<string, unknown>): string => {
  const baseUrl = getNZOSBaseUrl();
  let url = `${baseUrl}${endpoint}`;

  if (params) {
    const validParams = Object.keys(params).reduce(
      (acc, key) => {
        const value = params[key];
        if (value !== undefined && value !== null) {
          acc[key] = typeof value === 'string' ? value : JSON.stringify(value);
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    const searchParams = new URLSearchParams(validParams);
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  return url;
};

/**
 * Common error handling for NZOS API calls.
 */
export const handleNZOSError = async (response: Response, context: string): Promise<never> => {
  let errorMessage = `NZOS API Error in ${context}`;

  try {
    const errorData = await response.json();
    if (errorData.message || errorData.error) {
      errorMessage = errorData.message || errorData.error;
    }
  } catch {
    // If can't parse JSON, use status text
    errorMessage = response.statusText || errorMessage;
  }

  interface NZOSError extends Error {
    status?: number;
    statusText?: string;
  }

  const error = new Error(errorMessage) as NZOSError;
  error.status = response.status;
  error.statusText = response.statusText;

  throw error;
};
